// A camp that has been bought and has not landed yet.
//
// A front used to close and open in one command: `next-stage` swept the beaten
// army and put the next one down in the same beat. That is why a cleared front
// leaves the board **frozen** — the beaten side is on the clock with no move,
// so the player cannot take a turn until the camp arrives, and the branch
// picker has to be undismissable for exactly that reason.
//
// `clear-front` is the other half. It sweeps the army, hands the clock back and
// records what is owed; the camp lands on its own clock, and the turns between
// are the player's to spend.
//
// **One subtraction, three readers.** The reducer's gate, the chip that counts
// it down and any test that drives it all ask this — the despair banner's rule
// exactly, and for its reason: a countdown that disagrees with the thing it is
// counting down to is worse than no countdown at all.
//
// **The spawn clock lives here too** (§26 F10). Under `ruleset.spawn ===
// "clock"` a camp lands every `RULES.spawnEvery` rounds whether or not the last
// one is beaten — a second kind of owed camp, on a clock the run arms itself
// rather than one a `clear-front` bought. Beside `waveIn` because they are the
// same subtraction with the same three readers: `stageTurnsOver`, the
// `next-stage` gate, and the tests that drive both.

import type { GameState } from "../commands";
import { areEnemies, isOurs, livingSides } from "../position";
import { rulesetOf } from "../ruleset";
import { spawnIntervalFor } from "./mode";

/**
 * Rounds until the next camp may land, or `null` when none is owed.
 *
 * **Every clock here counts `Position.round`** (§31 T) — never `turnCounter`
 * (ticks) or `ply`, which a turn holds several or none of.
 *
 * Nought means *now* — the wave is due and `next-stage` will be accepted, which
 * is what `stageTurnsOver` answers true on. It never goes negative: a camp that
 * is late is still due now, and a chip counting past zero into minus numbers
 * would be reporting the driver being behind rather than anything about the
 * game.
 */
export function waveIn(state: GameState): number | null {
  const pending = state.run?.pending;
  if (!pending) return null;
  return Math.max(0, pending.until - state.position.round);
}

/**
 * Rounds until the spawn clock lands its camp, or `null` when it is not
 * running.
 *
 * `waveIn`'s reading exactly — nought means due now, never negative — for the
 * clock the run arms itself. Null everywhere outside a clock-mode run
 * (`ruleset.spawn === "clock"`), so the shipped game reads nothing and
 * nothing about it moves; null too while a `clear-front`
 * camp is owed (one owed camp at a time, whichever door bought it), and while
 * the clock is unarmed — the opening solo phase, a level that never fields an
 * enemy.
 *
 * The chip reads this beside `waveIn`, and `waveDue` — what the reducer's
 * gate and `stageTurnsOver` ask — is this subtraction reaching nought, so the
 * countdown on screen and the thing it counts down to cannot disagree.
 */
export function spawnIn(state: GameState): number | null {
  const run = state.run;
  if (!run || run.pending || !run.spawn) return null;
  if (rulesetOf(state).spawn !== "clock") return null;
  return Math.max(0, run.spawn.until - state.position.round);
}

/**
 * Is the spawn clock's camp due?
 *
 * `spawnIn` reaching nought — one subtraction, however many readers, which is
 * this file's whole rule. Reads `run.spawn`, which `armSpawnClock` sets and
 * the `next-stage` reducer clears, so an unarmed run is never due however
 * many turns pass.
 */
export function waveDue(state: GameState): boolean {
  return spawnIn(state) === 0;
}

/**
 * Arm the spawn clock, once the war has started.
 *
 * Called from `endTurn` — the one hook every turn-spending path goes through —
 * and a no-op everywhere but a clock-mode run whose clock is not already
 * running. **The war is the anchor**: a run opens with a solo phase (dig in,
 * no enemy anywhere), and a clock counted from turn nought would land its
 * first camp on top of whatever the level was doing — or, worse, land it
 * *already overdue* the moment the level's own first camp arrives. So the
 * clock starts when an enemy first stands on the board, and every landing
 * re-arms it from `next-stage` clearing this field.
 *
 * Replaces `next.run` rather than mutating it, like every other campaign pass
 * in `endTurn` — `run` is shared with the previous state.
 */
/**
 * Under the spawn clock, does this beaten front close *without* landing?
 *
 * Answers the `clear-front` delay that hands the next camp to the schedule,
 * or null when the ordinary boundary — sweep and land now — is right.
 *
 * **B's rule scoped to the last enemy.** "A beaten front still lands the next
 * camp early" is right when the beaten faction was the only one: the board
 * would otherwise sit empty until the clock ran out. With **another enemy
 * still standing** it makes two live factions permanent — beat one and its
 * replacement arrives at once, forever — so there the front merely closes:
 * the beaten army is swept, the clock is handed back, and the schedule keeps
 * the next camp. Reported from play.
 *
 * The delay is the schedule's own remainder — the armed clock's, or a camp
 * already owed (a second front beaten during a hold re-sets the same
 * `until`) — so closing a front this way moves no landing. Null when the
 * schedule is due now (`left <= 0`): the ordinary path lands the scheduled
 * camp *and* sweeps, which is the schedule being satisfied rather than
 * jumped. Null too for the run's own side and its allies (a beaten ally is
 * never a cleared front) and outside clock mode.
 */
export function spawnHoldsNext(state: GameState): number | null {
  const run = state.run;
  if (!run) return null;
  if (rulesetOf(state).spawn !== "clock") return null;
  if (state.status !== "checkmate" && state.status !== "stalemate") return null;
  const beaten = state.position.turn;
  if (isOurs(state.position, beaten, run.side)) return null;
  const another = livingSides(state.position).some(
    (side) => side !== beaten && areEnemies(state.position, side, run.side)
  );
  if (!another) return null;
  const until = run.pending?.until ?? run.spawn?.until;
  if (until === undefined) return null;
  const left = until - state.position.round;
  return left > 0 ? left : null;
}

export function armSpawnClock(next: GameState): void {
  const run = next.run;
  if (!run || run.spawn || run.pending) return;
  if (rulesetOf(next).spawn !== "clock") return;
  const war = livingSides(next.position).some((side) =>
    areEnemies(next.position, side, run.side)
  );
  if (!war) return;
  next.run = {
    ...run,
    // The interval is the run's, not a constant: Hard Mode scales it at this
    // read site (§26 F13) rather than forking `RULES.spawnEvery`, so `spawnIn`
    // and `waveDue` keep reading the one stored `until` and the countdown on
    // screen cannot disagree with the thing it counts down to.
    spawn: { until: next.position.round + spawnIntervalFor(run) }
  };
}
